import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import clsx from 'clsx';
import { useModal } from '../../modal-context';
import { useThreekit } from '../../threekit-context';
import ThreekitIcons from '../threekit-icons';

function Stick({ direction, stickHeight }) {
  const { darkMode } = useThreekit();
  let height;
  let flexDirection;
  const backgroundColor = darkMode ? 'bg-white' : 'bg-black';

  if (stickHeight === 'large') {
    height = direction === 'left' || direction === 'right'
      ? 'h-[1px] w-[20px] laptop:w-[118px]'
      : 'w-[1px] h-[20px] laptop:h-[118px]';
  } else if (stickHeight === 'medium') {
    height = direction === 'left' || direction === 'right'
      ? 'h-[1px] w-[20px] laptop:w-[64px]'
      : 'w-[1px] h-[20px] laptop:h-[64px]';
  } else {
    height = direction === 'left' || direction === 'right'
      ? 'h-[1px] w-[20px] laptop:w-[46px]'
      : 'w-[1px] h-[20px] laptop:h-[46px]';
  }

  if (direction === 'down') {
    flexDirection = 'flex-col-reverse';
  } else if (direction === 'left') {
    flexDirection = 'flex-row';
  } else if (direction === 'right') {
    flexDirection = 'flex-row-reverse';
  } else {
    flexDirection = 'flex-col';
  }

  return (
    <div className={clsx('flex items-center', flexDirection)}>
      <div className={clsx(backgroundColor, 'w-[11px] h-[11px] rounded-sm')} />
      <div className={clsx(backgroundColor, 'rounded-sm relative', height)} />
    </div>
  );
}

function TextWithStick({ direction, stickHeight, children }) {
  let flexDirection;

  if (direction === 'down') {
    flexDirection = 'flex-col-reverse';
  } else if (direction === 'left') {
    flexDirection = 'flex-row';
  } else if (direction === 'right') {
    flexDirection = 'flex-row-reverse';
  } else {
    flexDirection = 'flex-col';
  }

  return (
    <span className={`flex ${flexDirection} text-center laptop:gap-1.5`}>
      <Stick direction={direction} stickHeight={stickHeight} />
      {children}
    </span>
  );
}

function HelpOverlayModal() {
  const { closeModal, modalName } = useModal();
  const { inspo, productTag, darkMode } = useThreekit();
  const [hasSmallHeight, setHasSmallHeight] = useState(
    window.innerHeight < 660,
  );

  useEffect(() => {
    function handleResize() {
      setHasSmallHeight(window.innerHeight < 660);
    }

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hasInspirationObjects = inspo.some(
    (obj) => obj.customProductTag === productTag,
  );

  if (modalName !== 'Help') {
    return null;
  }

  const textClasses = (classes) =>
    clsx(
      darkMode ? 'text-white' : 'text-black',
      'font-r-10 laptop:font-r-12',
      classes,
    );

  return (
    <div
      onClick={() => closeModal()}
      className="absolute top-0 w-full h-full z-1"
    >
      <div className="fixed left-0 right-0 flex flex-col items-center justify-center bottom-1/2 laptop:top-[-100px] laptop:bottom-0 laptop:gap-3">
        <span className="w-[40px] laptop:w-auto">
          <ThreekitIcons
            type="rotationIcon"
            className={clsx(darkMode ? 'text-white' : 'text-black')}
          />
        </span>
        <span className={textClasses('max-w-[154px] text-center')}>
          Rotate the knife and select areas to customize
        </span>
      </div>
      <div className="fixed flex justify-center top-[68px] laptop:top-[80px] left-0 right-0">
        <TextWithStick
          direction="up"
          stickHeight={hasSmallHeight ? 'extra-small' : 'small'}
        >
          <span className={textClasses('max-w-[170px]')}>
            Not sure where to start? You can follow these steps
          </span>
        </TextWithStick>
      </div>
      <div className="fixed right-[186px] top-[80px] hidden laptop:block">
        <TextWithStick direction="up">
          <span className={textClasses('max-w-[90px]')}>
            Save and share your designs
          </span>
        </TextWithStick>
      </div>
      <div className="absolute right-[42px] top-[-250px] hidden laptop:block">
        <TextWithStick direction="down" stickHeight="large">
          <span className={textClasses('max-w-[121px]')}>
            Shuffle through designs
          </span>
        </TextWithStick>
      </div>
      <div className="fixed right-[10px] top-[68px] laptop:hidden">
        <TextWithStick direction="up" stickHeight="large">
          <span className={textClasses('max-w-[121px]')}>Shuffle</span>
        </TextWithStick>
      </div>
      {hasInspirationObjects && (
        <div className="absolute right-[165px] top-[-180px] hidden laptop:block">
          <TextWithStick direction="down">
            <span className={textClasses('max-w-[150px]')}>
              Explore our favorite customized designs
            </span>
          </TextWithStick>
        </div>
      )}
      <div
        className={clsx(
          hasSmallHeight ? 'laptop:top-[-120px]' : 'laptop:top-[-155px]',
          'absolute left-0 right-0 flex justify-center top-[-115px]',
        )}
      >
        <TextWithStick
          direction="down"
          stickHeight={hasSmallHeight ? 'extra-small' : 'small'}
        >
          <span className={textClasses('max-w-[165px]')}>
            All of the customization you can make for each part
          </span>
        </TextWithStick>
      </div>
      <div className="absolute left-[20px] justify-center top-[-180px] hidden laptop:block">
        <TextWithStick direction="down">
          <span className={textClasses('max-w-[140px]')}>
            Toggle between front and back of the knife
          </span>
        </TextWithStick>
      </div>
      <div className="fixed bottom-[calc(50vh+40px)] laptop:bottom-1/2 translate-y-[595%] left-[32px] laptop:left-[20px] laptop:hidden">
        <TextWithStick direction="left">
          <span className={textClasses('max-w-[140px]')}>Front and back</span>
        </TextWithStick>
      </div>
      <div className="fixed bottom-[calc(50vh+40px)] laptop:-translate-y-[50%] laptop:bottom-1/2 -translate-y-[260%] left-[32px] laptop:left-[60px]">
        <TextWithStick direction="left">
          <span className={textClasses('max-w-[115px]')}>
            Toggle between background colors
          </span>
        </TextWithStick>
      </div>
    </div>
  );
}

export default HelpOverlayModal;
